import { CREDENTIAL_HASHES } from "./adminCredentials";

const XOR_KEY = [0x4a, 0x9f, 0x2c, 0xe1, 0x77, 0x3b, 0x88, 0x5d];
const PEPPER_PARTS = [0x65, 0x76, 0x74, 0x5f, 0x6d, 0x74, 0x67, 0x5f, 0x37, 0x6b];

function deobfuscate(encoded) {
  return encoded.map((b, i) => b ^ XOR_KEY[i % XOR_KEY.length]);
}

function getPepper() {
  return String.fromCharCode(...PEPPER_PARTS);
}

export function constantTimeEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a[i] ^ b[i];
  }
  return diff === 0;
}

async function digestText(value) {
  const data = new TextEncoder().encode(value + getPepper());
  const hash = await crypto.subtle.digest("SHA-256", data);
  return new Uint8Array(hash);
}

async function digestBytes(parts) {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const merged = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    merged.set(part, offset);
    offset += part.length;
  }
  const hash = await crypto.subtle.digest("SHA-256", merged);
  return new Uint8Array(hash);
}

function toBase64Url(bytes) {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(str) {
  const padded = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const binary = atob(padded + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export async function hashCredential(value) {
  return digestText(value);
}

export async function verifyCredentialHash(input, expectedObfuscated) {
  const computed = await hashCredential(input);
  const expected = deobfuscate(expectedObfuscated);
  return constantTimeEqual(computed, expected);
}

function expiresToBytes(expires) {
  const bytes = new Uint8Array(8);
  let n = expires;
  for (let i = 7; i >= 0; i -= 1) {
    bytes[i] = n & 0xff;
    n = Math.floor(n / 256);
  }
  return bytes;
}

async function getSessionMaterial() {
  const userBytes = deobfuscate(CREDENTIAL_HASHES.user);
  const passBytes = deobfuscate(CREDENTIAL_HASHES.pass);
  return digestBytes([userBytes, passBytes]);
}

export async function createSessionToken() {
  const expires = Date.now() + 8 * 60 * 60 * 1000;
  const nonce = crypto.getRandomValues(new Uint8Array(16));
  const material = await getSessionMaterial();
  const token = await digestBytes([material, expiresToBytes(expires), nonce]);
  return {
    expires,
    nonce: toBase64Url(nonce),
    token: toBase64Url(token),
  };
}

export async function verifySessionToken(session) {
  if (!session?.expires || !session?.nonce || !session?.token) return false;
  if (Date.now() > session.expires) return false;

  const nonce = fromBase64Url(session.nonce);
  const material = await getSessionMaterial();
  const expected = await digestBytes([material, expiresToBytes(session.expires), nonce]);
  const received = fromBase64Url(session.token);
  return constantTimeEqual(expected, received);
}
